import {plural} from "@lingui/core/macro";
import {compose} from "mu-lambda";
import * as React from "react";

import ReactOdometer from "#odometer-package/odometer.tsx";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import BadgeCounts from "./badge-counts.tsx";

const pointsThreshold = 100000;
const shortDuration = 3000;
const longDuration = 15000;

type Props = {
    profileModel: ProfileModelType;
};

type State = {
    points: number;
    odometerDuration: number;
    odometerTheme: "minimal" | "slow";
};

/**
 * Badge counts and energy points that appear on the top right side of the
 * dashboard.
 */
class UserPointsAndBadges extends React.Component<Props, State> {
    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQ
    _isMounted: boolean;

    constructor(props: Props) {
        super(props);
        this.state = {
            points: props.profileModel.get("points"),
            odometerDuration: shortDuration,
            odometerTheme: "minimal",
        };
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;

        // Initialize the odometer.
        this.updatePoints();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    /**
     * Update the user's total points with a spinny odometer.
     */
    updatePoints() {
        // See TaskCounter.updateNumTasks for an explanation of this
        // early-return.
        if (!this._isMounted) {
            return;
        }

        const newPoints = this.props.profileModel.get("points");
        const oldPoints = this.state.points;

        const useLongDuration = newPoints - oldPoints > pointsThreshold;
        const duration = useLongDuration ? longDuration : shortDuration;
        const theme = useLongDuration ? "slow" : "minimal";

        this.setState({
            points: newPoints,
            odometerDuration: duration,
            odometerTheme: theme,
        });
    }

    render(): null | React.ReactElement<React.ComponentProps<"div">> {
        const {profileModel} = this.props;
        if (!profileModel) {
            return null;
        }

        const points = this.state.points;
        const url = this.props.profileModel.get("profileRoot") + "badges";

        const energyPoints = (
            <span
                className="energy-points-container"
                title={plural(points, {
                    one: "# energy point",
                    other: "# energy points",
                })}
            >
                <span className="energy-points-badge fixed-width">
                    {this.props.profileModel.isSal() ? (
                        <span>&infin;</span>
                    ) : (
                        <ReactOdometer
                            value={points}
                            duration={this.state.odometerDuration}
                            theme={this.state.odometerTheme}
                        />
                    )}
                </span>
            </span>
        );

        return (
            <div className="user-summary-view__points-and-badges">
                {energyPoints}

                {/*
                HACK (josh): We're using floats to keep this stuff positioned,
                and for some reason setting float: none tends to cause it to
                clip its children's padding. Super weird fix, but addign an
                empty span fixes it.
            */}
                <span />

                <div className="user-summary-view__points-and-badges__badges">
                    <BadgeCounts
                        counts={this.props.profileModel.get("badgeCounts")}
                        url={url}
                    />
                </div>
            </div>
        );
    }
}

export default compose(
    withBackboneModels((props: Props) => [props.profileModel]),
)(UserPointsAndBadges) as React.ComponentType<
    JSX.LibraryManagedAttributes<
        typeof UserPointsAndBadges,
        React.ComponentProps<typeof UserPointsAndBadges>
    >
>;
